import type { AxiosRequestConfig } from "axios";

import { getErrorMessage } from "@/utils/errorHandling";
import type { ApiError } from "@/types/api";

type StubResult =
    | { data: unknown }
    | {
          error: ApiError;
      };

type StubArgs = {
    normalizedMethod: string;
    normalizedUrl: string;
    normalizedParams: Record<string, unknown>;
    data: AxiosRequestConfig["data"];
};

export const handleAiInsightsEntitlementStub = async (
    args: StubArgs,
): Promise<StubResult | null> => {
    if (
        import.meta.env.VITE_ADMIN_AI_INSIGHTS_API_MODE !== "stub" ||
        args.normalizedUrl !== "/admin/v2/settings/entitlements"
    ) {
        return null;
    }

    const { aiInsightsEntitlementStubStore } = await import(
        "@/services/aiInsightsEntitlementStub"
    );

    if (args.normalizedMethod === "GET") {
        return {
            data: aiInsightsEntitlementStubStore.list({
                organisationId:
                    typeof args.normalizedParams.organisationId === "string"
                        ? args.normalizedParams.organisationId
                        : undefined,
                includeGlobal:
                    args.normalizedParams.includeGlobal === true ||
                    args.normalizedParams.includeGlobal === "true" ||
                    args.normalizedParams.includeGlobal === "1",
                limit:
                    typeof args.normalizedParams.limit === "number" ||
                    typeof args.normalizedParams.limit === "string"
                        ? Number.parseInt(String(args.normalizedParams.limit), 10)
                        : undefined,
                offset:
                    typeof args.normalizedParams.offset === "number" ||
                    typeof args.normalizedParams.offset === "string"
                        ? Number.parseInt(String(args.normalizedParams.offset), 10)
                        : undefined,
            }),
        };
    }

    if (args.normalizedMethod !== "POST") {
        return null;
    }

    const requestBody = (args.data ?? {}) as {
        organisationId?: string;
        entitlementKey?: string;
        isEnabled?: boolean;
        source?: "admin" | "system" | "stripe";
    };

    if (requestBody.entitlementKey !== "ai_insights") {
        return {
            error: {
                status: 400,
                data: "Stub mode supports only entitlementKey=ai_insights",
                message: "Stub mode supports only entitlementKey=ai_insights",
            },
        };
    }

    if (
        typeof requestBody.organisationId !== "string" ||
        requestBody.organisationId.trim().length === 0
    ) {
        return {
            error: {
                status: 400,
                data: "organisationId is required",
                message: "organisationId is required",
            },
        };
    }

    if (typeof requestBody.isEnabled !== "boolean") {
        return {
            error: {
                status: 400,
                data: "isEnabled must be a boolean",
                message: "isEnabled must be a boolean",
            },
        };
    }

    try {
        return {
            data: aiInsightsEntitlementStubStore.upsert({
                organisationId: requestBody.organisationId,
                isEnabled: requestBody.isEnabled,
                source: requestBody.source ?? "admin",
            }),
        };
    } catch (error) {
        return {
            error: {
                status: 400,
                data: getErrorMessage(error),
                message: "Stub request failed",
            },
        };
    }
};

export const handleDemoAccessStub = async (
    args: StubArgs,
): Promise<StubResult | null> => {
    if (
        import.meta.env.VITE_ADMIN_DEMO_ACCESS_API_MODE !== "stub" ||
        !args.normalizedUrl.startsWith("/admin/demo-access")
    ) {
        return null;
    }

    const { demoAccessStubStore } = await import("@/services/demoAccessStub");

    if (
        args.normalizedMethod === "GET" &&
        args.normalizedUrl === "/admin/demo-access"
    ) {
        return { data: demoAccessStubStore.list() };
    }

    if (
        args.normalizedMethod === "POST" &&
        args.normalizedUrl === "/admin/demo-access"
    ) {
        try {
            return {
                data: demoAccessStubStore.grant(
                    (args.data ?? {}) as {
                        firebaseId?: string;
                        email?: string;
                    },
                ),
            };
        } catch (error) {
            return {
                error: {
                    status: 400,
                    data: getErrorMessage(error),
                    message: "Stub request failed",
                },
            };
        }
    }

    if (
        args.normalizedMethod === "DELETE" &&
        args.normalizedUrl.startsWith("/admin/demo-access/")
    ) {
        const firebaseId = args.normalizedUrl.replace("/admin/demo-access/", "");

        if (!firebaseId) {
            return {
                error: {
                    status: 400,
                    data: "Firebase UID is required",
                    message: "Firebase UID is required",
                },
            };
        }

        const revoked = demoAccessStubStore.revoke({ firebaseId });

        if (!revoked) {
            return {
                error: {
                    status: 404,
                    data: "User not found",
                    message: "User not found",
                },
            };
        }

        return { data: revoked };
    }

    return null;
};
